import { useForm, Controller } from 'react-hook-form';
import {
  Sparkles,
  GripVertical,
  Trash2,
  Loader2,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ClpInput } from '@/components/common/clp-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Condition = 'nuevo' | 'como_nuevo' | 'buen_estado' | 'usado';
type Category =
  | 'muebles'
  | 'electronica'
  | 'electrodomesticos'
  | 'ropa'
  | 'deportes'
  | 'hogar'
  | 'libros'
  | 'juguetes'
  | 'vehiculos'
  | 'otros';

interface PhotoItem {
  id: string;
  url: string;
  /** Original filename for accessibility */
  name?: string;
}

/** Which fields were auto-filled by AI (used for "Suggested" badges) */
interface AiSuggestions {
  title?: boolean;
  description?: boolean;
  category?: boolean;
  condition?: boolean;
  price?: boolean;
}

interface ListingFormValues {
  title: string;
  description: string;
  category: Category;
  condition: Condition;
  price: number;
}

interface ListingFormProps {
  /** Pre-filled values (from AI or from editing an existing listing) */
  defaultValues?: Partial<ListingFormValues>;
  /** Photos already uploaded via the AiUploadZone */
  photos: PhotoItem[];
  /** Which fields were suggested by AI (shows sparkle badge) */
  aiSuggestions?: AiSuggestions;
  /** Whether the form is submitting */
  isSubmitting?: boolean;
  /** Callback to reorder photos (receives new ordered array) */
  onReorderPhotos?: (photos: PhotoItem[]) => void;
  /** Callback to delete a photo */
  onDeletePhoto?: (photoId: string) => void;
  /** Callback on valid submit */
  onSubmit: (values: ListingFormValues) => void;
  /** Callback to cancel/discard */
  onCancel?: () => void;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'muebles', label: 'Muebles' },
  { value: 'electronica', label: 'Electronica' },
  { value: 'electrodomesticos', label: 'Electrodomesticos' },
  { value: 'ropa', label: 'Ropa y accesorios' },
  { value: 'deportes', label: 'Deportes' },
  { value: 'hogar', label: 'Hogar y jardin' },
  { value: 'libros', label: 'Libros y medios' },
  { value: 'juguetes', label: 'Juguetes y ninos' },
  { value: 'vehiculos', label: 'Vehiculos y partes' },
  { value: 'otros', label: 'Otros' },
];

const CONDITIONS: { value: Condition; label: string; hint: string }[] = [
  { value: 'nuevo', label: 'Nuevo', hint: 'Sin usar, con o sin empaque' },
  { value: 'como_nuevo', label: 'Como nuevo', hint: 'Usado pocas veces, sin marcas' },
  { value: 'buen_estado', label: 'Buen estado', hint: 'Uso normal, funcional' },
  { value: 'usado', label: 'Usado', hint: 'Con desgaste visible' },
];

/* ------------------------------------------------------------------ */
/*  AI suggestion badge                                                */
/* ------------------------------------------------------------------ */

function AiSuggestedBadge() {
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 rounded-sm bg-[var(--color-accent-50)] px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/20"
      title="Sugerido por IA — puedes editar"
    >
      <Sparkles className="h-2.5 w-2.5" />
      Sugerido
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Photo grid (reorderable)                                           */
/* ------------------------------------------------------------------ */

function PhotoGrid({
  photos,
  onDelete,
}: {
  photos: PhotoItem[];
  onReorder?: (photos: PhotoItem[]) => void;
  onDelete?: (id: string) => void;
}) {
  if (photos.length === 0) return null;

  return (
    <div className="space-y-2">
      <Label>
        Fotos
        <span className="ml-1 text-[0.6875rem] font-normal text-[var(--color-ink-faint)]">
          ({photos.length})
        </span>
      </Label>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
        {photos.map((photo, i) => (
          <div
            key={photo.id}
            className={cn(
              'group relative aspect-square overflow-hidden rounded border border-[var(--color-hairline)] bg-[var(--color-paper-dim)]',
              i === 0 && 'col-span-2 row-span-2 sm:col-span-2 sm:row-span-2',
            )}
          >
            <img
              src={photo.url}
              alt={photo.name ?? `Foto ${i + 1}`}
              className="h-full w-full object-cover"
            />
            {/* Drag handle + delete overlay */}
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-[var(--color-ink)]/60 to-transparent px-1.5 pb-1.5 pt-6 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                type="button"
                aria-label="Mover foto"
                className="grid h-6 w-6 place-items-center rounded text-[var(--color-paper)]/80 hover:text-[var(--color-paper)] cursor-grab"
              >
                <GripVertical className="h-3.5 w-3.5" />
              </button>
              {onDelete && (
                <button
                  type="button"
                  aria-label={`Eliminar ${photo.name ?? `foto ${i + 1}`}`}
                  onClick={() => onDelete(photo.id)}
                  className="grid h-6 w-6 place-items-center rounded text-[var(--color-paper)]/80 hover:text-[var(--color-danger)] transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {/* "Cover" badge on first photo */}
            {i === 0 && (
              <span className="absolute left-1.5 top-1.5 rounded-sm bg-[var(--color-ink)]/70 px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-[0.06em] text-[var(--color-paper)] backdrop-blur-sm">
                Portada
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="text-[0.6875rem] text-[var(--color-ink-faint)]">
        Arrastra para reordenar. La primera imagen sera la portada del aviso.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Condition selector (visual radio group)                            */
/* ------------------------------------------------------------------ */

function ConditionSelector({
  value,
  onChange,
  aiSuggested,
}: {
  value: Condition;
  onChange: (v: Condition) => void;
  aiSuggested?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label>
        Condicion
        {aiSuggested && <AiSuggestedBadge />}
      </Label>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {CONDITIONS.map((c) => (
          <button
            key={c.value}
            type="button"
            role="radio"
            aria-checked={value === c.value}
            onClick={() => onChange(c.value)}
            className={cn(
              'flex flex-col items-start gap-0.5 rounded-md border px-3 py-2.5 text-left transition-all duration-150',
              value === c.value
                ? 'border-[var(--color-primary)] bg-[var(--color-primary-50)] ring-1 ring-[var(--color-primary)]/20'
                : 'border-[var(--color-hairline-strong)] bg-[var(--color-paper)] hover:border-[var(--color-ink-faint)] hover:bg-[var(--color-paper-dim)]',
            )}
          >
            <span
              className={cn(
                'text-sm font-semibold',
                value === c.value
                  ? 'text-[var(--color-primary)]'
                  : 'text-[var(--color-ink)]',
              )}
            >
              {c.label}
            </span>
            <span className="text-[0.6875rem] leading-snug text-[var(--color-ink-muted)]">
              {c.hint}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main form                                                          */
/* ------------------------------------------------------------------ */

/**
 * ListingForm
 * -----------
 * Post-AI-analysis form for creating or editing a marketplace listing.
 * Fields come pre-filled from AI analysis, each marked with a sparkle
 * "Sugerido" badge so users know what the AI contributed.
 *
 * Layout:
 * ┌────────────────────────────────────────┐
 * │  Photo grid (reorderable thumbnails)    │
 * ├────────────────────────────────────────┤
 * │  Title input         [Sugerido badge]  │
 * │  Description textarea [Sugerido badge] │
 * │  ┌Category select┐  ┌Price input┐     │
 * │  └───────────────┘  └────────────┘     │
 * │  Condition selector (4 radio cards)     │
 * ├────────────────────────────────────────┤
 * │  [Cancelar]              [Publicar]    │
 * └────────────────────────────────────────┘
 *
 * Follows existing form patterns from residente-form.tsx:
 * - react-hook-form + controlled fields
 * - Label + Input + error message pattern
 * - hairline border footer with action buttons
 */
export function ListingForm({
  defaultValues,
  photos,
  aiSuggestions,
  isSubmitting = false,
  onReorderPhotos,
  onDeletePhoto,
  onSubmit,
  onCancel,
  className,
}: ListingFormProps) {
  const form = useForm<ListingFormValues>({
    defaultValues: {
      title: defaultValues?.title ?? '',
      description: defaultValues?.description ?? '',
      category: defaultValues?.category ?? 'otros',
      condition: defaultValues?.condition ?? 'buen_estado',
      price: defaultValues?.price ?? 0,
    },
  });

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className={cn('space-y-6', className)}
    >
      {/* ---- AI analysis banner ---- */}
      {aiSuggestions && Object.values(aiSuggestions).some(Boolean) && (
        <div className="flex items-start gap-3 rounded-md border border-[var(--color-accent)]/20 bg-[var(--color-accent-50)] p-4">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
          <div>
            <p className="text-sm font-semibold text-[var(--color-ink)]">
              Campos sugeridos por IA
            </p>
            <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-[var(--color-ink-muted)]">
              Revisa y ajusta los campos marcados con{' '}
              <span className="inline-flex items-center gap-0.5 text-[var(--color-accent)]">
                <Sparkles className="h-2.5 w-2.5" />
                Sugerido
              </span>
              . Tu siempre tienes la ultima palabra.
            </p>
          </div>
        </div>
      )}

      {/* ---- Photo grid ---- */}
      <PhotoGrid
        photos={photos}
        onReorder={onReorderPhotos}
        onDelete={onDeletePhoto}
      />

      {/* ---- Title ---- */}
      <div>
        <Label htmlFor="listing-title">
          Titulo del aviso
          {aiSuggestions?.title && <AiSuggestedBadge />}
        </Label>
        <Input
          id="listing-title"
          className="mt-1.5"
          placeholder="Ej: Bicicleta de montana Shimano aro 26"
          maxLength={80}
          {...form.register('title', { required: 'El titulo es obligatorio' })}
        />
        <div className="mt-1 flex items-center justify-between">
          {form.formState.errors.title ? (
            <p className="text-xs text-[var(--color-danger)]">
              {form.formState.errors.title.message}
            </p>
          ) : (
            <span />
          )}
          <span className="text-[0.6875rem] text-[var(--color-ink-faint)]">
            {form.watch('title')?.length ?? 0}/80
          </span>
        </div>
      </div>

      {/* ---- Description ---- */}
      <div>
        <Label htmlFor="listing-desc">
          Descripcion
          {aiSuggestions?.description && <AiSuggestedBadge />}
        </Label>
        <textarea
          id="listing-desc"
          rows={4}
          maxLength={1000}
          placeholder="Describe el estado, tamano, motivo de venta..."
          className={cn(
            'mt-1.5 flex w-full rounded border border-[var(--color-hairline-strong)] bg-[var(--color-paper)] px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] shadow-[inset_0_1px_0_rgba(0,0,0,0.02)] transition-[border-color,box-shadow] duration-150 ease-out',
            'resize-y min-h-[100px]',
            'focus-visible:outline-none focus-visible:border-[var(--color-primary)] focus-visible:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_20%,transparent)]',
          )}
          {...form.register('description')}
        />
        <div className="mt-1 flex justify-end">
          <span className="text-[0.6875rem] text-[var(--color-ink-faint)]">
            {form.watch('description')?.length ?? 0}/1000
          </span>
        </div>
      </div>

      {/* ---- Category + Price row ---- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Category */}
        <div>
          <Label>
            Categoria
            {aiSuggestions?.category && <AiSuggestedBadge />}
          </Label>
          <Controller
            control={form.control}
            name="category"
            rules={{ required: 'Selecciona una categoria' }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {form.formState.errors.category && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.category.message}
            </p>
          )}
        </div>

        {/* Price */}
        <div>
          <Label htmlFor="listing-price">
            Precio (CLP)
            {aiSuggestions?.price && <AiSuggestedBadge />}
          </Label>
          <Controller
            control={form.control}
            name="price"
            rules={{
              validate: (v) => (v > 0 ? true : 'Ingresa un precio mayor a 0'),
            }}
            render={({ field }) => (
              <ClpInput
                id="listing-price"
                className="mt-1.5"
                value={field.value}
                onChange={field.onChange}
                placeholder="50.000"
              />
            )}
          />
          {form.formState.errors.price && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.price.message}
            </p>
          )}
          <p className="mt-1 flex items-center gap-1 text-[0.6875rem] text-[var(--color-ink-faint)]">
            <Info className="h-3 w-3" />
            Puedes poner $0 si quieres regalar el articulo
          </p>
        </div>
      </div>

      {/* ---- Condition selector ---- */}
      <Controller
        control={form.control}
        name="condition"
        render={({ field }) => (
          <ConditionSelector
            value={field.value}
            onChange={field.onChange}
            aiSuggested={aiSuggestions?.condition}
          />
        )}
      />

      {/* ---- Actions ---- */}
      <div className="flex items-center justify-end gap-2 border-t border-[var(--color-hairline)] pt-4">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="submit" variant="accent" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Publicando...
            </>
          ) : (
            'Publicar aviso'
          )}
        </Button>
      </div>
    </form>
  );
}
